import { create } from "zustand"
import { persist } from "zustand/middleware"
import { useEffect, useState } from "react"

export interface CartItem {
  promptId: string
  addedAt: Date
}

export interface Purchase {
  promptId: string
  purchasedAt: Date
}

export interface User {
  id: string
  email: string
  nickname: string
  profileImage: string
}

/** 이메일 기준 사용자별 데이터 (같은 이메일 재로그인 시 복원) */
type UserCarts = Record<string, CartItem[]>
type UserPurchases = Record<string, Purchase[]>
type UserProfiles = Record<string, { nickname: string; profileImage: string }>

interface StoreState {
  // Auth
  user: User | null
  isLoggedIn: boolean
  login: (user: User) => void
  logout: () => void
  updateUser: (updates: Partial<User>) => void

  // Cart (현재 로그인 사용자 기준, userCarts에서 동기화)
  cart: CartItem[]
  addToCart: (promptId: string) => void
  removeFromCart: (promptId: string) => void
  isInCart: (promptId: string) => boolean
  clearCart: () => void

  // Purchases (현재 로그인 사용자 기준, userPurchases에서 동기화)
  purchases: Purchase[]
  addPurchase: (promptIds: string[]) => void
  hasPurchased: (promptId: string) => boolean

  // 이메일 기준 사용자별 데이터 (persist, 재로그인 시 복원)
  userCarts: UserCarts
  userPurchases: UserPurchases
  userProfiles: UserProfiles
}

/** 이메일을 기준 키로 사용 (같은 이메일 재로그인 시 데이터 복원) */
function userKey(user: User | null): string {
  return user?.email ?? ""
}

export const useStore = create<StoreState>()(
  persist(
    (set, get) => ({
      // Auth state
      user: null,
      isLoggedIn: false,
      userCarts: {},
      userPurchases: {},
      userProfiles: {},

      login: (user) =>
        set((state) => {
          const key = user.email
          const profile = state.userProfiles[key]
          const mergedUser: User = {
            ...user,
            nickname: profile?.nickname ?? user.nickname,
            profileImage: profile?.profileImage ?? user.profileImage,
          }
          return {
            user: mergedUser,
            isLoggedIn: true,
            cart: state.userCarts[key] ?? [],
            purchases: state.userPurchases[key] ?? [],
          }
        }),

      logout: () =>
        set({
          user: null,
          isLoggedIn: false,
          cart: [],
          purchases: [],
        }),

      updateUser: (updates) =>
        set((state) => {
          if (!state.user) return state
          const newUser = { ...state.user, ...updates }
          const key = state.user.email
          return {
            user: newUser,
            userProfiles: {
              ...state.userProfiles,
              [key]: {
                nickname: newUser.nickname,
                profileImage: newUser.profileImage,
              },
            },
          }
        }),

      // Cart state (이메일 기준 userCarts 동기화)
      cart: [],

      addToCart: (promptId) =>
        set((state) => {
          if (!state.user) return state
          if (get().purchases.some((p) => p.promptId === promptId)) return state
          const key = userKey(state.user)
          const newCart = [...state.cart, { promptId, addedAt: new Date() }]
          return {
            cart: newCart,
            userCarts: { ...state.userCarts, [key]: newCart },
          }
        }),

      removeFromCart: (promptId) =>
        set((state) => {
          if (!state.user) return state
          const key = userKey(state.user)
          const newCart = state.cart.filter((item) => item.promptId !== promptId)
          return {
            cart: newCart,
            userCarts: { ...state.userCarts, [key]: newCart },
          }
        }),

      isInCart: (promptId) => get().cart.some((item) => item.promptId === promptId),

      clearCart: () =>
        set((state) => {
          if (!state.user) return state
          const key = userKey(state.user)
          return {
            cart: [],
            userCarts: { ...state.userCarts, [key]: [] },
          }
        }),

      // Purchase state (이메일 기준 userPurchases 동기화, 구매분 장바구니에서 제거)
      purchases: [],

      addPurchase: (promptIds) =>
        set((state) => {
          if (!state.user) return state
          const key = userKey(state.user)
          const purchasedSet = new Set(promptIds)
          const newPurchases = [
            ...state.purchases,
            ...promptIds.map((promptId) => ({
              promptId,
              purchasedAt: new Date(),
            })),
          ]
          const newCart = state.cart.filter((item) => !purchasedSet.has(item.promptId))
          return {
            purchases: newPurchases,
            cart: newCart,
            userPurchases: { ...state.userPurchases, [key]: newPurchases },
            userCarts: { ...state.userCarts, [key]: newCart },
          }
        }),

      hasPurchased: (promptId) => get().purchases.some((p) => p.promptId === promptId),
    }),
    {
      name: "prompt-market-storage",
      partialize: (state) => ({
        user: state.user,
        isLoggedIn: state.isLoggedIn,
        userCarts: state.userCarts,
        userPurchases: state.userPurchases,
        userProfiles: state.userProfiles,
      }),
      merge: (persisted, current) => {
        const p = persisted as Partial<StoreState>
        const merged = { ...current, ...p }
        const key = merged.user?.email ?? ""
        if (merged.user && key) {
          merged.cart = merged.userCarts?.[key] ?? []
          merged.purchases = merged.userPurchases?.[key] ?? []
          const profile = merged.userProfiles?.[key]
          if (profile) {
            merged.user = {
              ...merged.user,
              nickname: profile.nickname,
              profileImage: profile.profileImage,
            }
          }
        } else {
          merged.cart = []
          merged.purchases = []
        }
        return merged
      },
    }
  )
)

// Hook to handle hydration - prevents flash of incorrect content
export const useHydration = () => {
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    setHydrated(true)
  }, [])

  return hydrated
}
